import NegativeX from 'assets/images/cubemaps/GalaxyTex_NegativeX.jpg';
import NegativeY from 'assets/images/cubemaps/GalaxyTex_NegativeY.jpg';
import NegativeZ from 'assets/images/cubemaps/GalaxyTex_NegativeZ.jpg';
import PositiveX from 'assets/images/cubemaps/GalaxyTex_PositiveX.jpg';
import PositiveY from 'assets/images/cubemaps/GalaxyTex_PositiveY.jpg';
import PositiveZ from 'assets/images/cubemaps/GalaxyTex_PositiveZ.jpg';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

import { EngineConfig } from '../types';

/**
 * Manages the THREE.js scene, camera, renderer, controls, and bloom composer.
 * Ported from: helpers/sceneSetup.ts, helpers/bloomSetup.ts, useThreeScene.ts
 *
 * No React dependencies. Created once, destroyed once.
 */
export class SceneManager {
  readonly scene: THREE.Scene;
  readonly portalScene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly composer: EffectComposer;
  readonly controls: OrbitControls;

  private resizeHandler: () => void;

  constructor(
    private container: HTMLDivElement,
    config: EngineConfig
  ) {
    // Scene
    this.scene = new THREE.Scene();
    this.createSkybox();
    this.scene.fog = new THREE.FogExp2(0x000011, 0.002);

    // Camera (space.html original params)
    const aspect = container.clientWidth / container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 10000);
    this.camera.position.set(0, 60, 120);
    this.camera.lookAt(0, 0, 0);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    container.appendChild(this.renderer.domElement);

    // Portal: separate scene, rendered by main renderer after depth restore.
    // Portal materials use depthTest:true + toneMapped:false.
    this.portalScene = new THREE.Scene();
    this.portalScene.background = null;
    this.portalScene.fog = null;

    // OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 10;
    this.controls.maxDistance = 1500;
    this.controls.maxPolarAngle = Math.PI / 1.5;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 0.3;

    // Bloom (adaptive brightness — matches original)
    this.composer = this.setupBloom(config);

    // Resize
    this.resizeHandler = () => this.resize();
    window.addEventListener('resize', this.resizeHandler);
  }

  private createSkybox(): void {
    const cubeTextureLoader = new THREE.CubeTextureLoader();
    const skyboxTexture = cubeTextureLoader.load([
      PositiveX,
      NegativeX,
      PositiveY,
      NegativeY,
      PositiveZ,
      NegativeZ,
    ]);
    this.scene.background = skyboxTexture;
  }

  private setupBloom(config: EngineConfig): EffectComposer {
    const composer = new EffectComposer(this.renderer);
    composer.addPass(new RenderPass(this.scene, this.camera));

    // Adaptive bloom brightness (matches original app.js)
    const screenPixels =
      window.innerWidth * window.innerHeight * Math.min(window.devicePixelRatio, 2);
    const bloomScale = Math.min(1.0, 2100000 / screenPixels);
    const adaptedStrength = config.bloomStrength * bloomScale;
    const adaptedThreshold = config.bloomThreshold + (1 - bloomScale) * 0.25;

    // Reduced-resolution bloom — bloom is a blurry effect by nature,
    // lower res is visually near-indistinguishable but far fewer pixels to process.
    const isMobile = window.innerWidth <= 768;
    const bloomResScale = isMobile ? 0.125 : 0.25;
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(
        Math.round(window.innerWidth * bloomResScale),
        Math.round(window.innerHeight * bloomResScale)
      ),
      adaptedStrength,
      config.bloomRadius,
      adaptedThreshold
    );
    composer.addPass(bloomPass);

    return composer;
  }

  private resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
  }

  destroy(): void {
    window.removeEventListener('resize', this.resizeHandler);

    // Remove DOM elements
    if (this.renderer.domElement.parentNode) {
      this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
    }

    // Dispose scene children
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Points) {
        object.geometry.dispose();
        const mat = object.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    });

    // Dispose composer passes
    this.composer.passes.forEach((pass) => {
      if ('dispose' in pass && typeof (pass as { dispose?: () => void }).dispose === 'function') {
        (pass as { dispose: () => void }).dispose();
      }
    });

    this.controls.dispose();
    this.renderer.dispose();

    while (this.scene.children.length > 0) {
      this.scene.remove(this.scene.children[0]);
    }
  }
}
